import React, { useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import loadable from '@loadable/component'
import Spin from 'antd/lib/spin'
import isEmpty from 'lodash/isEmpty'
import QueryString from 'qs'
import { COOKIE_KEYS, STATUS, getCookie, addScript, isActiveFeature } from '@features/General'
import useChangeEvent from '@hooks/processor/useChangeEvent'
import routes from '@utils/constants/routes'
import { RootState } from '@app-store/store'
import { rootActions } from '@app-store/slices'
import { getBundles, getExtraKeys } from '@features/General'
import { getSubDomain, isValidSubdomain } from '@features/Auth'
import usePath from '@hooks/processor/usePath'
import useWebsocket from '@hooks/websocket/useWebsocket'
import { AppContext } from 'context'
import { chatServerSocketURL } from 'config'
import { tokenRoutes, preSelectedEventRoutes, stepLayoutRoutes } from '@features/General/constants/routes'
import { eventColorHelper } from '@utils/helpers/commonHelpers'
import SupportFloatingIcons from '@components/Auth/Support/FloatingIcons'
import LoginModal from 'components/Auth/LoginModal'

const ChatSider = loadable(() => import('@components/Chat/Sider'))
export const BasicLayout = loadable(() =>
    import('@components/Layouts/LayoutProvider').then(module => ({ default: module.BasicLayout }))
)
export const OrganizerLayout = loadable(() =>
    import('@components/Layouts/LayoutProvider').then(module => ({ default: module.OrganizerLayout }))
)

const LayoutProvider = props => {
    const { children } = props
    const params = QueryString.parse(location.search, { ignoreQueryPrefix: true })
    const { path, pathRoute, pathEquality } = usePath()
    const { connected, ws } = useWebsocket(chatServerSocketURL)
    const { callChangeEventEndpoints } = useChangeEvent()
    const dispatch = useDispatch()
    const eventId = getCookie(COOKIE_KEYS.ALA_EVENT)
    const hasToken = !isEmpty(getCookie(COOKIE_KEYS.ALA_TOKEN))
    const [preparing, setPreparing] = React.useState<boolean>(true)
    const { direction } = useSelector((state: RootState) => state.localization.languageType)
    const { data: surveyRecord } = useSelector((state: RootState) => state.survey.oneSurveyByToken)
    const { eventChanged } = useSelector((state: RootState) => state.ui.authSlider)
    const { data: eventByDomainData, status: fetchBrandingStatus } = useSelector(
        (state: RootState) => state.events.oneEventByDomain
    )

    const isTokenRoute = React.useMemo(() => pathEquality(tokenRoutes), [pathRoute])

    useEffect(() => {
        const considerAsHub = pathEquality(['/subscription/payment']) && !!params?.plan
        const subdomain = getSubDomain(considerAsHub)
        if (eventId && !isTokenRoute) {
            if (hasToken && !eventChanged) {
                callChangeEventEndpoints({
                    eventId,
                    eventChanged: false
                })
            }
            addScript('https://js.stripe.com/v3/')
            if (subdomain !== 'leap') {
                addScript('build/tpl/user-guiding.js')
            }
        }
        if (isValidSubdomain(considerAsHub) && !eventByDomainData) {
            dispatch(
                rootActions.events.oneEventByDomain.onRequest({
                    subdomain,
                    sagaCB: {
                        onSuccess: response => {
                            eventColorHelper(response?.branding)
                        }
                    }
                })
            )
        }
    }, [eventId])

    useEffect(() => {
        const callLocalizationApi = (language = 'en') => {
            const payload = { bundle: getBundles(path) }
            const extraKeys = getExtraKeys(path)
            if (extraKeys.length) {
                Object.assign(payload, { extraKeys })
            }
            if (isTokenRoute || !eventId || !hasToken) {
                // If it is a token path we pass language to call another endpoint for getting localization data by language
                // The endpoint doesn't need token, so it will not return 401 (Unautorized) error and will not break the system
                Object.assign(payload, { language })
            }
            dispatch(rootActions.localization.locals.onRequest(payload))
        }
        if (!pathEquality(['/survey/survey-info'])) {
            callLocalizationApi()
        } else if (surveyRecord?._id) {
            callLocalizationApi(surveyRecord?.language?.[0] || 'en')
        }
    }, [isTokenRoute, pathRoute, surveyRecord])

    useEffect(() => {
        if (direction) {
            const dir = direction.toLowerCase()
            document.documentElement.dir = dir
            document.body.setAttribute('class', dir)
            const layout = document.getElementsByClassName('ant-layout-has-sider')
            if (layout.length) {
                layout[0].classList.remove('ant-layout-rtl')
            }
            setTimeout(() => {
                if (fetchBrandingStatus !== STATUS.RUNNING) {
                    setPreparing(false)
                }
            }, 100)
        }
        if (pathEquality(['/login', '/support', '/activation', '/matching/match_list/select'])) {
            if (fetchBrandingStatus !== STATUS.RUNNING) {
                setPreparing(false)
            }
        }
    }, [direction, path, fetchBrandingStatus])

    const renderLayout = () => {
        switch (true) {
            case path.includes(routes.meeting_request.request._):
            case path.includes(routes.organizer.events.details._):
            case path.includes(routes.open_events._):
            case path.includes(routes.directory.company_profile.itp.new._):
            case path.includes(routes.pipeline.trade_hub.rfq_market.create._):
            case path.includes(routes.pipeline.trade_hub.quotations.create._):
            case path.includes(routes.pipeline.trade_hub.quotations.send._):
            case path.includes(routes.matching.match_list_select._):
            case path.includes(routes.sync_calendar._):
            case path.includes(routes.sync_calendar.successful_calendar._):
            case path.includes(routes.matching.match_list_select.sync_calendar._):
            case path.includes(routes.matching.match_list_select.availability_calendar._):
            case path.includes(routes.matching.match_list_select.successful_calendar._):
            case path.includes(routes.meetings.wish_campaign.meeting_request._):
            case path.includes(routes.settings.communications.whats_new.detail._):
            case path.includes(routes.settings.event.company_badges.preview._):
            case path.includes(routes.settings.event.badges.preview):
            case path.includes(routes.meetings.meetingInfo._):
            case path.includes(routes.exhibitor.onboarding.calendar._.replace(':state', 'google')):
            case path.includes(routes.exhibitor.onboarding.calendar._.replace(':state', 'calendly')):
            case path.includes(routes.exhibitor.onboarding.calendar._.replace(':state', 'outlook')):
            case path.includes(routes.subscription._) && !path.includes(routes.settings.subscriptions._):
            case path.includes(routes.itp.itp_profile._):
            case path.includes(routes.meeting_request.login._):
            case path.includes(routes.meeting_request.accept._):
            case path.includes(routes.meeting_request.reject._):
            case path.includes(routes.unsubscribe._):
                return <>{children}</>
            case path === routes.organizer.events._:
            case path === routes.join_aladdinb2b._:
            case path.includes(routes.meeting_request._):
                return <BasicLayout>{children}</BasicLayout>
            case path === routes.organizer._:
            case path === routes.organizer.new_dashboard:
            case path.includes(routes.directory._):
            case path.includes(routes.meetings._):
            case path.includes(routes.matching._):
            case path.includes(routes.settings._):
            case path.includes(routes.pipeline._):
            case path.includes(routes.user._):
            case path.includes(routes.analytics._):
            case path.includes(routes.alarms._):
            case path.includes(routes.pipeline.trade_hub.rfq_market._):
                return <OrganizerLayout>{children}</OrganizerLayout>
            default:
                return <>{children}</>
        }
    }

    return (
        <AppContext.Provider value={{ connected, ws }}>
            {!pathEquality(preSelectedEventRoutes) && !!eventId && isActiveFeature('Chat') && <ChatSider />}
            <SupportFloatingIcons isStepLayout={pathEquality(stepLayoutRoutes)} isTokenRoute={isTokenRoute} />
            {!pathEquality(preSelectedEventRoutes) && <LoginModal />}
            {preparing && (
                <div className="absolute-block">
                    <Spin spinning={preparing} />
                </div>
            )}
            {renderLayout()}
        </AppContext.Provider>
    )
}

export default LayoutProvider
